import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { TopNav } from '../components/top-nav.js';
import {
  AdminForbiddenError,
  type AdminLLMCostsByAgent,
  type AdminLLMCostsByOrg,
  type AdminLLMCostsByRole,
  type AdminLLMCostsSpendPoint,
  type AdminTokenEconomicsByOrg,
  type MeResponse,
} from '../lib/api.js';
import { formatNumber } from '../lib/format.js';
import { adminLLMCostsQuery, meQuery } from '../lib/queries.js';

// s180 admin LLM-costs page (WS6 of the LLM router margin plan).
// Revived in Phase 0 of the meter-with-overage spec
// (the LLM meter/overage doc (CommitMind)) — the founder + ops use this to
// watch real cost trends before Phase 1 ships per-org metering.
//
// Hero metric is the cache hit rate per the margin plan's "what the
// savings get used for" section — the leading indicator that s177/s179
// actually moved spend. Supporting widgets give the per-role + per-org
// breakdown plus a daily spend trend.
//
// API gate (MAGIG_ADMIN_EMAILS) is the source of truth — the route's
// beforeLoad just verifies sign-in. Non-admins land here and get a
// forbidden panel inside the page rather than a route-level 403, so
// the UX matches the rest of the dashboard's admin gating.

export const Route = createFileRoute('/admin/llm-costs')({
  beforeLoad: async ({ context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    return { me };
  },
  loader: ({ context }) => context as { me: MeResponse },
  component: AdminLLMCostsRoutePage,
});

function AdminLLMCostsRoutePage() {
  const { me } = Route.useLoaderData();
  return <AdminLLMCostsPage me={me} />;
}

type WindowDays = 1 | 7 | 30;
const WINDOW_OPTIONS: { days: WindowDays; label: string }[] = [
  { days: 1, label: '24h' },
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
];

export function AdminLLMCostsPage({ me }: { me: MeResponse }) {
  const [windowDays, setWindowDays] = useState<WindowDays>(7);
  const summary = useQuery(adminLLMCostsQuery(windowDays));

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <TopNav active={null} />

      <section className="mx-auto max-w-[1000px] px-4 py-12 sm:px-6">
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
          ↘ admin · llm costs
        </p>
        <h1 className="mt-3 font-display text-[38px] sm:text-[56px] font-black leading-[1] tracking-[-0.04em]">
          mag's spend, all of it.
        </h1>
        <p className="mt-3 text-[15px] leading-[1.6] text-[var(--ink)]/70">
          global aggregates across <code className="font-mono text-[13px]">llm_calls</code>. cache
          hit rate is the metric to watch — it's the leading indicator that the s177/s179 wiring
          moved real dollars. signed in as {me.email}.
        </p>

        <div className="mt-6 flex items-center gap-2">
          {WINDOW_OPTIONS.map((opt) => (
            <button
              key={opt.days}
              type="button"
              onClick={() => setWindowDays(opt.days)}
              className={`rounded-full border-[1.5px] px-3 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] transition ${
                opt.days === windowDays
                  ? 'border-[var(--ink)] bg-[var(--ink)] text-[var(--cream)]'
                  : 'border-[var(--line-strong)] bg-white text-[var(--ink)]/70 hover:text-[var(--ink)]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {summary.isPending ? (
          <p className="mt-12 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
            ↘ loading…
          </p>
        ) : summary.isError && summary.error instanceof AdminForbiddenError ? (
          <ForbiddenPanel />
        ) : summary.isError ? (
          <ErrorPanel message={summary.error instanceof Error ? summary.error.message : 'failed'} />
        ) : summary.data ? (
          <>
            <HeroMetric data={summary.data.totals} />
            <SpendTrend points={summary.data.spendTrend} />
            <ByRoleTable rows={summary.data.byRole} />
            <ByAgentTable rows={summary.data.byAgent} />
            <ByOrgTable rows={summary.data.byOrg} />
            <TokenEconomicsByOrgTable rows={summary.data.tokenEconomicsByOrg} />
          </>
        ) : null}

        <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--ink)]/55">
          ↘{' '}
          <Link to="/home" className="link-plum">
            back to the feed
          </Link>
        </p>
      </section>
    </main>
  );
}

function HeroMetric({
  data,
}: {
  data: {
    cacheHitRate: number;
    costUsd: string;
    calls: number;
    inputTokens: number;
    cachedTokens: number;
    outputTokens: number;
  };
}) {
  const hitPct = (data.cacheHitRate * 100).toFixed(1);
  // Counter-factual: what the cached tokens would have cost at the
  // un-cached input rate vs. what they actually cost at the cached
  // rate. Rough — uses an avg input/cached ratio rather than per-model
  // pricing — but right order of magnitude for "look, this saved $X".
  // Math: assume cached billed at 0.1× input. Savings ≈ 0.9 × (cached
  // share of cost basis). For a dashboard headline, that's enough.
  const cachedShare = data.inputTokens > 0 ? data.cachedTokens / data.inputTokens : 0;
  const savingsEstimateUsd = Number(data.costUsd) * cachedShare * 9; // 0.9×/0.1× ratio
  return (
    <div className="mt-8 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-6 shadow-[4px_4px_0_var(--spell)]">
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
          ↘ cache hit rate
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
          tokens served from cache
        </span>
      </div>
      <p className="mt-2 font-display text-[80px] font-black leading-none tracking-[-0.04em]">
        {hitPct}%
      </p>
      <p className="mt-3 text-[14px] leading-[1.5] text-[var(--ink)]/70">
        {formatNumber(data.cachedTokens)} of {formatNumber(data.inputTokens)} input tokens came from
        the cache. roughly{' '}
        <span className="font-mono font-semibold text-[var(--ink)]">
          ${savingsEstimateUsd.toFixed(2)}
        </span>{' '}
        saved vs. the un-cached path.
      </p>
      <div className="mt-5 flex flex-wrap items-baseline gap-x-8 gap-y-3">
        <Stat label="calls" value={formatNumber(data.calls)} />
        <Stat label="actual spend" value={formatUsd(data.costUsd)} />
        <Stat label="output tokens" value={formatNumber(data.outputTokens)} />
      </div>
    </div>
  );
}

function SpendTrend({ points }: { points: AdminLLMCostsSpendPoint[] }) {
  if (points.length === 0) return null;
  const maxCost = points.reduce((m, p) => Math.max(m, Number(p.costUsd)), 0);
  return (
    <>
      <h2 className="mt-10 font-display text-[20px] font-bold tracking-[-0.02em]">
        spend over time
      </h2>
      <p className="mt-1 text-[13px] leading-[1.5] text-[var(--ink)]/65">
        daily totals. bar height is cost; the small mono number under each bar is that day's cache
        hit rate.
      </p>
      <div className="mt-4 rounded-xl border border-[var(--line)] bg-white p-5">
        <div className="flex h-[180px] items-end gap-1">
          {points.map((p) => {
            const cost = Number(p.costUsd);
            const heightPct = maxCost > 0 ? (cost / maxCost) * 100 : 0;
            return (
              <div
                key={p.date}
                className="flex flex-1 flex-col items-center justify-end"
                title={`${p.date} · ${formatUsd(p.costUsd)} · ${p.calls} calls · ${(p.cacheHitRate * 100).toFixed(0)}% cached`}
              >
                <div
                  className="w-full rounded-t bg-[var(--ink)]"
                  style={{ height: `${heightPct}%`, minHeight: cost > 0 ? '2px' : '0' }}
                />
              </div>
            );
          })}
        </div>
        <div className="mt-2 flex gap-1">
          {points.map((p) => (
            <div key={`label-${p.date}`} className="flex-1 text-center">
              <p className="font-mono text-[9px] uppercase tracking-[0.1em] text-[var(--ink)]/45">
                {p.date.slice(5)}
              </p>
              <p className="font-mono text-[9px] uppercase tracking-[0.1em] text-[var(--plum)]/70">
                {(p.cacheHitRate * 100).toFixed(0)}%
              </p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

const ROLE_LABEL: Record<string, string> = {
  orchestrator: 'orchestrator · opus',
  concept: 'concept · sonnet',
  critic: 'critic · sonnet',
  generation: 'generation · haiku',
  performance: 'performance · haiku',
  routine: 'routine · haiku',
};

function ByRoleTable({ rows }: { rows: AdminLLMCostsByRole[] }) {
  if (rows.length === 0) return null;
  return (
    <>
      <h2 className="mt-10 font-display text-[20px] font-bold tracking-[-0.02em]">
        by role × model
      </h2>
      <div className="mt-3 flex flex-col gap-3">
        {rows.map((r) => (
          <article
            key={`${r.role}-${r.model}`}
            className="rounded-xl border border-[var(--line)] bg-white p-5"
          >
            <div className="flex items-baseline justify-between">
              <span className="font-mono text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]">
                {ROLE_LABEL[r.role] ?? r.role}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
                {r.model}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap items-baseline gap-x-8 gap-y-3">
              <Stat label="cost" value={formatUsd(r.costUsd)} />
              <Stat label="calls" value={formatNumber(r.calls)} />
              <Stat label="cache hit" value={`${(r.cacheHitRate * 100).toFixed(1)}%`} />
              <Stat
                label="tokens (in/out)"
                value={`${formatNumber(r.inputTokens)} / ${formatNumber(r.outputTokens)}`}
              />
            </div>
          </article>
        ))}
      </div>
    </>
  );
}

function ByAgentTable({ rows }: { rows: AdminLLMCostsByAgent[] }) {
  if (rows.length === 0) return null;
  return (
    <>
      <h2 className="mt-10 font-display text-[20px] font-bold tracking-[-0.02em]">by agent</h2>
      <p className="mt-1 text-[13px] leading-[1.5] text-[var(--ink)]/65">
        the specific agents driving spend inside each role. avg input tokens flags fat-context
        agents — candidates for the tool-migration plan in{' '}
        <code className="font-mono text-[12px]">the LLM tool migration notes (CommitMind)</code>.
        rows with high tokens + low cache hit are the biggest wins.
      </p>
      <div className="mt-3 flex flex-col gap-2">
        {rows.map((r) => (
          <div
            key={`${r.agentName}-${r.role}`}
            className="flex items-baseline justify-between rounded-lg border border-[var(--line)] bg-white px-4 py-3"
          >
            <div className="min-w-0">
              <span className="font-mono text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]">
                {r.agentName}
              </span>
              <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/45">
                {r.role}
              </span>
            </div>
            <div className="flex items-baseline gap-6">
              <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--ink)]/60">
                {r.calls} {r.calls === 1 ? 'call' : 'calls'} · {formatNumber(r.avgInputTokens)} avg
                in · {(r.cacheHitRate * 100).toFixed(0)}% cached
              </span>
              <span className="font-display text-[18px] font-bold tracking-[-0.015em]">
                {formatUsd(r.costUsd)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function ByOrgTable({ rows }: { rows: AdminLLMCostsByOrg[] }) {
  if (rows.length === 0) return null;
  return (
    <>
      <h2 className="mt-10 font-display text-[20px] font-bold tracking-[-0.02em]">
        top organizations
      </h2>
      <p className="mt-1 text-[13px] leading-[1.5] text-[var(--ink)]/65">
        the 10 orgs burning the most LLM dollars this window. unattributed rows are internal /
        cross-brand jobs (playbook mining, etc.).
      </p>
      <div className="mt-3 flex flex-col gap-2">
        {rows.map((r) => (
          <div
            key={r.organizationId || 'unattributed'}
            className="flex items-baseline justify-between rounded-lg border border-[var(--line)] bg-white px-4 py-3"
          >
            <span className="font-mono text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]">
              {r.organizationName ?? '(unattributed)'}
            </span>
            <div className="flex items-baseline gap-6">
              <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--ink)]/60">
                {r.calls} {r.calls === 1 ? 'call' : 'calls'} · {(r.cacheHitRate * 100).toFixed(0)}%
                cached
              </span>
              <span className="font-display text-[18px] font-bold tracking-[-0.015em]">
                {formatUsd(r.costUsd)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// [s264] Per-org token economics table — operational view on per-customer
// profitability. Pairs with ByOrgTable above: cost_usd there = COGS,
// topupRevenueUsd here = revenue (excluding subscription, which is
// monthly and the plan column tells you the rate). "Net" surfaces
// granted - consumed in the window so you see who's ahead vs burning.
function TokenEconomicsByOrgTable({ rows }: { rows: AdminTokenEconomicsByOrg[] }) {
  if (rows.length === 0) return null;
  return (
    <>
      <h2 className="mt-10 font-display text-[20px] font-bold tracking-[-0.02em]">
        token economics
      </h2>
      <p className="mt-1 text-[13px] leading-[1.5] text-[var(--ink)]/65">
        the 20 orgs by tokens consumed in this window. granted = subscription + top-up + auto-buy
        deltas; consumed = sum of llm_decrement deltas. pair with the cost table above for the
        margin math (topup revenue here vs anthropic pass-through there).
      </p>
      <div className="mt-3 overflow-x-auto rounded-lg border border-[var(--line)] bg-white">
        <table className="w-full min-w-[640px] text-[12.5px]">
          <thead className="border-b border-[var(--line)] bg-[var(--paper)] text-left font-mono text-[10.5px] uppercase tracking-[0.1em] text-[var(--ink)]/65">
            <tr>
              <th className="px-3 py-2 font-semibold">org</th>
              <th className="px-3 py-2 font-semibold">plan</th>
              <th className="px-3 py-2 text-right font-semibold">balance</th>
              <th className="px-3 py-2 text-right font-semibold">granted</th>
              <th className="px-3 py-2 text-right font-semibold">consumed</th>
              <th className="px-3 py-2 text-right font-semibold">net</th>
              <th className="px-3 py-2 text-right font-semibold">top-up revenue</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--line)]">
            {rows.map((r) => {
              const net = r.tokensGranted - r.tokensConsumed;
              const netCls =
                net > 0
                  ? 'text-[var(--plum)]'
                  : net < 0
                    ? 'text-[var(--pulse)]'
                    : 'text-[var(--ink)]/75';
              return (
                <tr key={r.organizationId}>
                  <td className="px-3 py-2 font-medium text-[var(--ink)]">
                    {r.organizationName ?? '(unnamed)'}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/65">
                    {r.plan}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {formatNumber(r.balanceTokens)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-[var(--ink)]/75">
                    +{formatNumber(r.tokensGranted)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-[var(--ink)]/75">
                    −{formatNumber(r.tokensConsumed)}
                  </td>
                  <td className={`px-3 py-2 text-right font-mono tabular-nums ${netCls}`}>
                    {net > 0 ? '+' : ''}
                    {formatNumber(net)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    ${Number(r.topupRevenueUsd).toFixed(0)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ForbiddenPanel() {
  return (
    <div className="mt-12 rounded-2xl border border-dashed border-[var(--line-strong)] bg-[var(--paper)] p-8 text-center">
      <p className="font-display text-[24px] font-bold tracking-[-0.02em]">you can't see this.</p>
      <p className="mt-3 text-[14px] leading-[1.5] text-[var(--ink)]/70">
        this page is gated behind <code className="font-mono">MAGIG_ADMIN_EMAILS</code>. your email
        isn't in that list. if you should be, ask ops to add you.
      </p>
    </div>
  );
}

function ErrorPanel({ message }: { message: string }) {
  return (
    <div className="mt-12 rounded-2xl border border-dashed border-[var(--line-strong)] bg-[var(--paper)] p-6">
      <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--plum)]">
        ↘ couldn't load
      </p>
      <p className="mt-2 font-mono text-[12px] text-[var(--ink)]/70">{message}</p>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
        {label}
      </span>
      <span className="font-display text-[20px] font-bold tracking-[-0.015em]">{value}</span>
    </div>
  );
}

function formatUsd(raw: string): string {
  const n = Number(raw);
  if (!Number.isFinite(n)) return '$0.00';
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}
